// ============================================================
//  DISPUTE RESOLVE PANEL — ADMIN DASHBOARD
//  Path: src/components/DisputeResolvePanel.tsx
//
//  NEW FILE. Shown on a task whose status is 'disputed' — a customer
//  disputed a job marked done, or a no-show report was sent to review
//  (someone checked in without location, so it couldn't be verified).
//  Three decisions, all made on the server (POST
//  /api/admin/tasks/:id/resolve-dispute), each recorded in the audit
//  log and the task timeline:
//
//    pay_expert       the work was done / the customer didn't show up
//                     → job completed, tasker paid
//    refund_customer  full refund, tasker not paid, nobody suspended
//    tasker_no_show   full refund AND the no-show suspension
//                     (24 h → 72 h → 1 week)
//
//  Only super_admin / admin may decide (money moves) — customer_service
//  sees the panel read-only. Read the timeline below the panel first:
//  it shows who checked in where and when.
//
//  HOLD-THEN-CHARGE: the customer's card is only HELD from the booking
//  until the job is confirmed. So "Pay the tasker" CHARGES the held card,
//  and the two refund choices RELEASE the hold (the customer is never
//  charged). The hold lapses on its own about 7 days after booking — if
//  it lapses before a decision, the customer is not charged and the
//  tasker cannot be paid through the app — so while the payment is on
//  hold this panel shows the deadline. Pass paymentStatus + hiredAt.
// ============================================================

import React, { useState } from 'react'
import { resolveDispute } from '../services/admin.service'
import { DisputeResolution } from '../types'

const MAX_NOTE_LENGTH = 1000

// Same hold window as the backend's AUTHORIZATION_WINDOW_DAYS (default 7,
// Stripe's own limit for card holds). Only used to SHOW a deadline.
const HOLD_WINDOW_DAYS = 7

// Wording depends on whether the card is only held (not charged yet) or was
// already charged.
const optionsFor = (held: boolean): { value: DisputeResolution; label: string; help: string; className: string }[] => [
  {
    value: 'pay_expert',
    label: 'Pay the tasker',
    help: held
      ? 'The work was done (or the customer didn’t show up). The customer’s card is charged now, the job is completed and the tasker is paid.'
      : 'The work was done (or the customer didn’t show up). The job is completed and the tasker is paid.',
    className: 'bg-green-600/10 text-green-400 hover:bg-green-600/20',
  },
  {
    value: 'refund_customer',
    label: held ? 'Release the hold (no charge)' : 'Refund the customer',
    help: held
      ? 'The hold on the customer’s card is released — they are never charged. The tasker is not paid. Nobody is suspended.'
      : 'Full refund to the customer. The tasker is not paid. Nobody is suspended.',
    className: 'bg-yellow-600/10 text-yellow-400 hover:bg-yellow-600/20',
  },
  {
    value: 'tasker_no_show',
    label: 'Tasker no-show',
    help: held
      ? 'The tasker didn’t show up. The card hold is released (no charge) AND the tasker is suspended (24 h → 72 h → 1 week).'
      : 'The tasker didn’t show up. Full refund AND the tasker is suspended (24 h → 72 h → 1 week).',
    className: 'bg-red-500/10 text-red-400 hover:bg-red-500/20',
  },
]

const DisputeResolvePanel: React.FC<{
  taskId:         string
  disputeReason?: string | null
  canResolve:     boolean              // super_admin / admin only
  onResolved:     () => void           // reload the task
  // The task's paymentStatus and hire time — when 'authorized' the customer's
  // card is only HELD, and the deadline below is shown.
  paymentStatus?: string | null
  hiredAt?:       string | null
}> = ({ taskId, disputeReason, canResolve, onResolved, paymentStatus, hiredAt }) => {
  const held = paymentStatus === 'authorized'
  const OPTIONS = optionsFor(held)
  // Approximate (hire time + the hold window): the exact moment is set by
  // the card network, so it is shown as "about".
  const holdEnds = held && hiredAt ? new Date(new Date(hiredAt).getTime() + HOLD_WINDOW_DAYS * 24 * 60 * 60 * 1000) : null
  const holdDaysLeft = holdEnds ? (holdEnds.getTime() - Date.now()) / (24 * 60 * 60 * 1000) : null
  const [note, setNote]         = useState('')
  const [busy, setBusy]         = useState<DisputeResolution | null>(null)
  const [error, setError]       = useState('')
  const [done, setDone]         = useState('')

  const decide = async (option: typeof OPTIONS[number]) => {
    if (!window.confirm(`${option.label}?\n\n${option.help}\n\nThis can't be undone.`)) return
    setBusy(option.value)
    setError('')
    try {
      const res = await resolveDispute(taskId, { resolution: option.value, note: note.trim() || undefined })
      setDone(res?.data?.message || 'Dispute resolved')
      onResolved()
    } catch (err: any) {
      console.error('Failed to resolve dispute:', (err as any)?.response?.status, (err as any)?.message)
      setError(err?.response?.data?.message || (err?.isNetworkError
        ? 'Could not reach the server — check your connection and try again.'
        : 'Could not resolve this dispute.'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mb-4 bg-red-500/5 border border-red-500/20 rounded-xl p-3">
      <p className="text-xs text-red-400 uppercase font-semibold mb-1">⚠️ Disputed — needs a decision</p>
      {disputeReason && <p className="text-xs text-gray-300 mb-2">{disputeReason}</p>}
      <p className="text-xs text-gray-500 mb-3">Check the activity timeline below — it shows who checked in, where and when.</p>

      {held && (
        <div className={`rounded-lg p-2.5 mb-3 text-xs border ${
          holdDaysLeft != null && holdDaysLeft < 2
            ? 'bg-red-500/10 border-red-500/30 text-red-300'
            : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300'
        }`}>
          <p className="font-semibold mb-0.5">⏳ The customer’s card is on hold — not charged yet</p>
          <p>
            {holdEnds
              ? <>The hold lapses about <strong>{holdEnds.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                  {holdDaysLeft != null && holdDaysLeft > 0 ? ` (${holdDaysLeft < 1 ? 'less than a day' : `${Math.floor(holdDaysLeft)} day${Math.floor(holdDaysLeft) === 1 ? '' : 's'}`} left)` : ' — it may already have lapsed'}.</>
              : <>The hold lapses about {HOLD_WINDOW_DAYS} days after booking.</>}
            {' '}Decide before then: after it lapses the customer is not charged and the tasker cannot be paid through the app.
          </p>
        </div>
      )}

      {done ? (
        <p className="text-sm text-green-400">{done}</p>
      ) : !canResolve ? (
        <p className="text-xs text-gray-500">Only an admin can resolve disputes.</p>
      ) : (
        <>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))} rows={2}
            placeholder="Note for the audit log (optional)"
            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-3 py-2 text-white text-xs mb-2 focus:border-brand-500 focus:outline-none resize-none" />
          <div className="space-y-2">
            {OPTIONS.map((o) => (
              <button key={o.value} onClick={() => decide(o)} disabled={busy !== null} title={o.help}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${o.className}`}>
                <span className="text-sm font-medium">{busy === o.value ? 'Working…' : o.label}</span>
                <span className="block text-[11px] text-gray-400">{o.help}</span>
              </button>
            ))}
          </div>
          {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
        </>
      )}
    </div>
  )
}

export default DisputeResolvePanel